import { useAtomValue } from 'jotai'
import { useMemo, useRef } from 'react'
import useMeasure from 'react-use-measure'
import { TransformerGizmoBorder } from './gizmo-border'
import { TransformerGizmoCorner } from './gizmo-corner'
import { getBoundingBox } from './math'
import { transformerPreviewStateAtom, transformerStateAtom } from './state'
import { useTransformerStore } from './transformer-store'

export type TransformerGizmoProps = {
	enableSnapping: boolean
	enableScaling: boolean
}

export function TransformerGizmo({ enableSnapping, enableScaling }: TransformerGizmoProps) {
	const store = useTransformerStore()

	const previewState = useAtomValue(transformerPreviewStateAtom, { store })
	const mainState = useAtomValue(transformerStateAtom, { store })

	const state = previewState ?? mainState

	const [ref, { width, height }] = useMeasure()

	const boundingBox = useMemo(() => {
		return getBoundingBox({
			x: state.offset.x, // this reflects the subject relative offset
			y: state.offset.y,
			w: width,
			h: height,
			scale: state.scale,
			rotate: state.rotation,
		})
	}, [state.offset.x, state.offset.y, width, height, state.scale, state.rotation])

	const transformedRef = useRef<HTMLDivElement>(null)

	const style = {
		transform:
			`translate(${state.offset.x}px, ${state.offset.y}px) scale(${state.scale}) rotate(${state.rotation}rad)`,
	}

	return (
		<>
			<div className="absolute inset-0 pointer-events-none" ref={ref}>
				{/* Invisible measurement element for center-point calculation */}
				<div
					className="absolute inset-0 pointer-events-none"
					style={style}
					ref={transformedRef}
				/>

				<TransformerGizmoBorder
					boundingBox={boundingBox}
					state={state}
					size={{ width, height }}
					enableSnapping={enableSnapping}
				/>

				<TransformerGizmoCorner
					transformedRef={transformedRef}
					boundingBox={boundingBox}
					state={state}
					originalSize={{ width, height }}
					position="topLeft"
					enableScaling={enableScaling}
				/>
				<TransformerGizmoCorner
					transformedRef={transformedRef}
					boundingBox={boundingBox}
					state={state}
					originalSize={{ width, height }}
					position="topRight"
					enableScaling={enableScaling}
				/>
				<TransformerGizmoCorner
					transformedRef={transformedRef}
					boundingBox={boundingBox}
					state={state}
					originalSize={{ width, height }}
					position="bottomRight"
					enableScaling={enableScaling}
				/>
				<TransformerGizmoCorner
					transformedRef={transformedRef}
					boundingBox={boundingBox}
					state={state}
					originalSize={{ width, height }}
					position="bottomLeft"
					enableScaling={enableScaling}
				/>
			</div>
		</>
	)
}
